import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { AppDispatch } from '../store/store';
import {
  addUploadedFile,
  updateUploadedFile,
  removeUploadedFile,
  clearUploadedFiles,
  UploadedFileMeta,
  clearTargetLanguages,
  clearTranslationData,
} from '../store/chatSlice';
import { hideGlobalBlocking, showGlobalBlocking } from '../store/loadingSlice';
import { setSelectedTool } from '../store/chatModeSlice';
import { utf8ToBase64, arrayBufferToBase64 } from '../utils/base64.utils';
import { convertFileToPdf } from '../utils/document/fileConversion.service';
import logger from '../utils/logger/logger';
import { useFileInputCancelListener } from './useFileInputCancelListener';
import type { StagedFile, FileWarning } from '../components/Chat/input/types';

// Re-export types the consumer may need
export type { StagedFile, FileWarning };

const MAX_MB = 5;

/** Aborts every active FileReader in the ref array and empties the array. */
function abortAndClearReaders(readersRef: React.RefObject<FileReader[]>) {
  readersRef.current.forEach(r => r.abort());
  readersRef.current = [];
}

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface UseChatFileUploadOptions {
  dispatch: AppDispatch;
  msalInstance: IPublicClientApplication;
  uploadedFiles: UploadedFileMeta[];
  selectedTool: string | null;
  sessionId: string | null;
  hasMessages?: boolean;
  onClearChat?: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  translationFileInputRef: React.RefObject<HTMLInputElement | null>;
  isFileUploadDialogOpen: boolean;
  setIsFileUploadDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isStreaming?: boolean;
  globalBlocking?: boolean;
  effectiveToolForSend?: string | null;
  shouldHideTools?: boolean;
  isRagEnabled?: boolean;
  setIsFileUploadActive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCompareActive: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTranslationCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  manuallyClearedToolRef: React.MutableRefObject<boolean>;
}

export interface UseChatFileUploadReturn {
  // State
  stagedFiles: StagedFile[];
  setStagedFiles: React.Dispatch<React.SetStateAction<StagedFile[]>>;
  uploadError: { file: string; message: string } | null;
  setUploadError: React.Dispatch<React.SetStateAction<{ file: string; message: string } | null>>;
  fileWarning: FileWarning | null;
  setFileWarning: React.Dispatch<React.SetStateAction<FileWarning | null>>;
  isDataAnalysisUploading: boolean;
  showImmediateSpinner: boolean;
  setShowImmediateSpinner: React.Dispatch<React.SetStateAction<boolean>>;
  awaitingFileSelection: boolean;
  setAwaitingFileSelection: React.Dispatch<React.SetStateAction<boolean>>;
  filesBeingProcessedRef: React.MutableRefObject<boolean>;
  isDraggingOver: boolean;
  setIsDraggingOver: React.Dispatch<React.SetStateAction<boolean>>;
  isFileDragInProgress: boolean;
  setIsFileDragInProgress: React.Dispatch<React.SetStateAction<boolean>>;
  showBlockingMessage: boolean;
  setShowBlockingMessage: React.Dispatch<React.SetStateAction<boolean>>;
  hideFileUploadDialogDropzone: boolean;
  setHideFileUploadDialogDropzone: React.Dispatch<React.SetStateAction<boolean>>;

  // Callbacks
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handlePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  handleRemoveFile: (fileName: string) => void;
  handleClearAllFiles: () => void;
  uploadFilesBatch: (files: { name: string; type: string; data: string }[]) => Promise<void>;
  cleanupCancel: () => void;
  getAllowedFileTypes: string;
  handleDragEnter: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleCancelDirectDropUpload: () => void;
  handleRemoveStagedFile: (fileName: string) => void;
  handleFilesDroppedOnDialog: (files: File[]) => void;
  handleFileUploadDialogDropzoneClick: () => void;
  handleFileUploadDialogUpload: () => void;
  handleFileUploadDialogClose: () => void;
  handleFileUploadDialogCancelFileRead: () => void;
  processPastedImagesFromFiles: (imageFiles: File[]) => void;
  computeFileLimits: (
    incomingFiles: File[],
    existingCount: number,
    opts: { isAnalysisMode: boolean; isTranslationMode: boolean; allImagesCombined: boolean }
  ) => {
    maxTotal: number;
    remainingSlots: number;
    hardLimitReached: boolean;
    effectiveFiles: File[];
    warning: { title: string; subtitle?: string | null; message: string; mode: 'general' | 'translation' } | null;
  };
  // Refs exposed for JSX/effects that reference them
  uploadedFilesRef: React.MutableRefObject<UploadedFileMeta[]>;
  isFileUploadDialogOpenRef: React.MutableRefObject<boolean>;
  stagedFilesRef: React.MutableRefObject<StagedFile[]>;
  translationActiveReadersRef: React.MutableRefObject<FileReader[]>;
  dialogActiveReadersRef: React.MutableRefObject<FileReader[]>;
  generalActiveReadersRef: React.MutableRefObject<FileReader[]>;
  directDropBatchFileNamesRef: React.MutableRefObject<string[]>;
  isDirectDropBatchActiveRef: React.MutableRefObject<boolean>;
  isFileDragInProgressRef: React.MutableRefObject<boolean>;
  isDraggingOverRef: React.MutableRefObject<boolean>;
}

// ─── Hook Implementation ───────────────────────────────────────────────────────

export function useChatFileUpload(options: UseChatFileUploadOptions): UseChatFileUploadReturn {
  const {
    dispatch,
    msalInstance,
    uploadedFiles,
    selectedTool,
    sessionId,
    hasMessages,
    onClearChat,
    fileInputRef,
    translationFileInputRef,
    isFileUploadDialogOpen,
    setIsFileUploadDialogOpen,
    isStreaming,
    globalBlocking,
    effectiveToolForSend,
    shouldHideTools,
    isRagEnabled,
    setIsFileUploadActive,
    setIsCompareActive,
    setIsTranslationCollapsed,
    manuallyClearedToolRef,
  } = options;

  // ─── State ─────────────────────────────────────────────────────────────────

  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [uploadError, setUploadError] = useState<{ file: string; message: string } | null>(null);
  const [fileWarning, setFileWarning] = useState<FileWarning | null>(null);
  const [awaitingFileSelection, setAwaitingFileSelection] = useState(false);
  const [showImmediateSpinner, setShowImmediateSpinner] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [showBlockingMessage, setShowBlockingMessage] = useState(false);
  const [isFileDragInProgress, setIsFileDragInProgress] = useState(false);
  const [hideFileUploadDialogDropzone, setHideFileUploadDialogDropzone] = useState(false);

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const filesBeingProcessedRef = React.useRef(false);
  const awaitingFileSelectionRef = React.useRef(false);
  const translationActiveReadersRef = React.useRef<FileReader[]>([]);
  const dialogActiveReadersRef = React.useRef<FileReader[]>([]);
  const generalActiveReadersRef = React.useRef<FileReader[]>([]);
  const directDropBatchFileNamesRef = React.useRef<string[]>([]);
  const isDirectDropBatchActiveRef = React.useRef(false);
  const isFileUploadDialogOpenRef = React.useRef(false);
  const stagedFilesRef = React.useRef<StagedFile[]>([]);
  const uploadedFilesRef = React.useRef(uploadedFiles);
  uploadedFilesRef.current = uploadedFiles;
  const isFileDragInProgressRef = React.useRef(false);
  const isDraggingOverRef = React.useRef(false);

  // ─── Derived State ─────────────────────────────────────────────────────────

  const isDataAnalysisUploading = selectedTool === 'data_analysis' &&
    (uploadedFiles.some(f => f.status === 'uploading' || f.status === 'pending') || awaitingFileSelection);

  // ─── Sync refs with state ──────────────────────────────────────────────────

  useEffect(() => {
    awaitingFileSelectionRef.current = awaitingFileSelection;
  }, [awaitingFileSelection]);

  useEffect(() => {
    isFileUploadDialogOpenRef.current = isFileUploadDialogOpen;
  }, [isFileUploadDialogOpen]);

  useEffect(() => {
    stagedFilesRef.current = stagedFiles;
  }, [stagedFiles]);

  useEffect(() => {
    isFileDragInProgressRef.current = isFileDragInProgress;
  }, [isFileDragInProgress]);

  useEffect(() => {
    isDraggingOverRef.current = isDraggingOver;
  }, [isDraggingOver]);

  // Hide global blocking when upload error surfaces
  useEffect(() => {
    if (uploadError) {
      dispatch(hideGlobalBlocking());
    }
  }, [uploadError, dispatch]);

  // Clear spinner when awaiting stops
  useEffect(() => {
    if (showImmediateSpinner && !awaitingFileSelection) {
      setShowImmediateSpinner(false);
      setIsDraggingOver(false);
      directDropBatchFileNamesRef.current = [];
      isDirectDropBatchActiveRef.current = false;
    }
  }, [showImmediateSpinner, awaitingFileSelection]);

  // Re-show dialog dropzone when upload finishes
  useEffect(() => {
    if (
      isFileUploadDialogOpen &&
      hideFileUploadDialogDropzone &&
      !awaitingFileSelection &&
      stagedFiles.length > 0 &&
      stagedFiles.every(file => file.status !== 'uploading')
    ) {
      setHideFileUploadDialogDropzone(false);
    }
  }, [isFileUploadDialogOpen, hideFileUploadDialogDropzone, awaitingFileSelection, stagedFiles]);

  // ─── Upload Endpoint Resolution ───────────────────────────────────────────

  const resolveUploadEndpoint = useCallback(() => {
    const apimBaseUrl = import.meta.env.VITE_APIM_BASE_URL || 'https://navigatordev.ai.3m.com';
    const envOverride = import.meta.env.VITE_FILE_UPLOAD_URL || undefined;
    const useLocal = import.meta.env.VITE_USE_LOCAL_FILE_UPLOAD === 'true';
    if (envOverride) return envOverride;
    if (useLocal) {
      return 'http://localhost:8006/ai/upload';
    }
    return `${apimBaseUrl}/nav-api/api/ai/file-upload`;
  }, []);

  // ─── uploadFilesBatch ─────────────────────────────────────────────────────

  const uploadFilesBatch = useCallback(async (fileMetaDirectArray: { name: string; type: string; data: string }[]) => {
    if (!fileMetaDirectArray || fileMetaDirectArray.length === 0) return;

    fileMetaDirectArray.forEach(m => {
      dispatch(updateUploadedFile({ name: m.name, changes: { status: 'uploading', errorMessage: undefined } }));
    });

    // Acquire token once
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'No authenticated account' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }
    const tokenRequest = { scopes: [`${import.meta.env.VITE_CLIENT_ID || '262b88ff-8a8d-49fc-8485-017906bd3c29'}/user_impersonation`], account: accounts[0] };
    let accessToken: string | undefined;
    try {
      accessToken = (await msalInstance.acquireTokenSilent(tokenRequest)).accessToken;
    } catch {
      try {
        accessToken = (await msalInstance.acquireTokenPopup(tokenRequest)).accessToken;
      } catch {
        setUploadError({ file: fileMetaDirectArray[0].name, message: 'Token acquisition failed' });
        dispatch(hideGlobalBlocking());
        fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
        return;
      }
    }
    if (!accessToken) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'No access token' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    // Build files array with base64 normalization for csv
    const filesPayload = fileMetaDirectArray.map(meta => {
      const looksBase64 = /^[A-Za-z0-9+/=\r\n]+$/.test(meta.data.substring(0, 64));
      let contentBase64: string;
      if (meta.type === 'text/csv' && !looksBase64) {
        try {
          contentBase64 = utf8ToBase64(meta.data);
        } catch {
          contentBase64 = btoa(meta.data);
        }
      } else {
        contentBase64 = meta.data;
      }
      return { filename: meta.name, content_base64: contentBase64 };
    });

    const uploadEndpoint = resolveUploadEndpoint();
    const stableSession = sessionId || 'unknown_session';
    const generatedRequestId = `upload_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    const generatedTimestamp = new Date().toISOString();
    const requestCore = {
      request_id: generatedRequestId,
      timestamp: generatedTimestamp,
      query_type: 'response-api',
      session: stableSession,
      prompt: 'file-upload-batch',
      metadata: { source: 'react-app' }
    };
    const requestPayload = {
      ...requestCore,
      payload: { ...requestCore },
      files: filesPayload
    };

    // Log sanitized batch payload
    try {
      const sanitized = {
        ...requestPayload,
        files: requestPayload.files.map(f => ({
          filename: f.filename,
          content_base64_head: f.content_base64.slice(0, 60) + (f.content_base64.length > 60 ? '…' : ''),
          content_base64_length: f.content_base64.length
        }))
      };
      logger.log('[upload-batch] request payload', sanitized);
      try {
        const rawString = JSON.stringify(requestPayload);
        logger.log('[upload-batch] raw-json-length', rawString.length);
        logger.log('[upload-batch] raw-json-string', rawString);
      } catch {/* ignore serialization issues */}
    } catch { /* empty */ }

    // Retry logic: attempt upload with at least one retry
    let resp: Response | undefined;
    const maxAttempts = 2;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        logger.log(`[upload-batch] Upload attempt ${attempt}/${maxAttempts}`);
        resp = await fetch(uploadEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestPayload)
        });

        if (resp) {
          logger.log(`[upload-batch] Upload succeeded on attempt ${attempt}`);
          break;
        }
      } catch (e) {
        logger.warn(`[upload-batch] Upload attempt ${attempt} failed:`, e);

        if (attempt < maxAttempts) {
          const retryDelay = 1000;
          logger.log(`[upload-batch] Waiting ${retryDelay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }

    if (!resp) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: 'Network error during batch upload after retries' });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    try {
      const rawClone = resp.clone();
      const rawText = await rawClone.text();
      const headersObject = Array.from(resp.headers.entries()).reduce((acc: Record<string, string>, [k, v]) => { acc[k] = v; return acc; }, {});
      const truncated = rawText.length > 2000 ? rawText.slice(0, 2000) + '…(truncated)' : rawText;
      logger.log('[upload-batch] raw response', { status: resp.status, ok: resp.ok, headers: headersObject, body_preview: truncated });
    } catch (err) {
      logger.warn('[upload-batch] failed raw log', err);
    }

    if (!resp.ok) {
      setUploadError({ file: fileMetaDirectArray[0].name, message: `Batch upload failed (HTTP ${resp.status})` });
      dispatch(hideGlobalBlocking());
      fileMetaDirectArray.forEach(f => dispatch(removeUploadedFile(f.name)));
      return;
    }

    let json: any = null;
    try { json = await resp.json(); } catch { /* empty */ }

    try {
      const uploadedFilesArray = json?.data?.content?.uploaded_files;
      const sanitizedResp = json ? {
        top_level_keys: Object.keys(json || {}),
        uploaded_files_mapping: Array.isArray(uploadedFilesArray) ? uploadedFilesArray.map((f: any) => ({ filename: f.filename, file_id: f.file_id, status: f.status })) : undefined,
        source: json?.source
      } : { raw: 'no-json' };
      logger.log('[upload-batch] response', sanitizedResp);
    } catch { /* empty */ }

    const multiUploaded = json?.data?.content?.uploaded_files;
    if (Array.isArray(multiUploaded) && multiUploaded.length > 0) {
      multiUploaded.forEach((f: any) => {
        if (f?.filename) {
          if (f?.file_id && f?.status === 'success') {
            dispatch(updateUploadedFile({ name: f.filename, changes: { status: 'uploaded', fileId: f.file_id } }));
          } else {
            dispatch(updateUploadedFile({ name: f.filename, changes: { status: 'error', errorMessage: f?.status || 'upload_failed' } }));
          }
        }
      });
      const missing = multiUploaded.filter((f: any) => !f.file_id);
      if (missing.length > 0) {
        setUploadError({ file: missing[0].filename || fileMetaDirectArray[0].name, message: 'One or more files missing file_id.' });
      }
      const allDone = multiUploaded.every((f: any) => f.file_id || f.status === 'error');
      if (allDone) {
        logger.log('[upload-batch] Upload complete - clearing awaiting states');
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      } else {
        logger.log('[upload-batch] Some files still uploading, keeping spinner visible');
      }
    } else {
      // Fallback: attempt recursive search for any file_id
      const gatherFileIds: string[] = [];
      const visit = (obj: any, depth = 0) => {
        if (!obj || depth > 6) return;
        if (Array.isArray(obj)) { obj.forEach(el => visit(el, depth + 1)); return; }
        for (const k of Object.keys(obj)) {
          const v = obj[k];
          if (k === 'file_id' && typeof v === 'string') gatherFileIds.push(v);
          if (v && typeof v === 'object') visit(v, depth + 1);
        }
      };
      visit(json);
      if (gatherFileIds.length === fileMetaDirectArray.length) {
        fileMetaDirectArray.forEach((m, i) => {
          dispatch(updateUploadedFile({ name: m.name, changes: { status: 'uploaded', fileId: gatherFileIds[i] } }));
        });
        const allDone = gatherFileIds.length === fileMetaDirectArray.length;
        if (allDone) {
          logger.log('[upload-batch] Upload complete (fallback path) - clearing awaiting states');
          setAwaitingFileSelection(false);
          filesBeingProcessedRef.current = false;
          dispatch(hideGlobalBlocking());
        } else {
          logger.log('[upload-batch] Some files still uploading (fallback), keeping spinner visible');
        }
      } else {
        setUploadError({ file: fileMetaDirectArray[0].name, message: 'Batch response did not include uploaded_files array.' });
        dispatch(hideGlobalBlocking());
      }
    }
  }, [msalInstance, dispatch, resolveUploadEndpoint, sessionId]);

  // ─── cleanupCancel ─────────────────────────────────────────────────────────

  const cleanupCancel = useCallback(() => {
    logger.log('[cleanupCancel] Called - Current state:', {
      filesBeingProcessed: filesBeingProcessedRef.current,
      awaitingFileSelection,
      selectedTool
    });
    filesBeingProcessedRef.current = false;
    setAwaitingFileSelection(false);
    setShowImmediateSpinner(false);
    dispatch(hideGlobalBlocking());
    logger.log('[cleanupCancel] Cleanup complete');
  }, [dispatch, selectedTool, awaitingFileSelection]);

  // Attach cancel listener to file input
  useFileInputCancelListener(fileInputRef, cleanupCancel);

  // Window focus listener for cancel detection
  useEffect(() => {
    const handleFocus = () => {
      if (!awaitingFileSelectionRef.current) {
        logger.log('[window focus] Not awaiting file selection, ignoring');
        return;
      }

      const hasValue = (fileInputRef.current?.value && fileInputRef.current.value !== '')
        || (translationFileInputRef.current?.value && translationFileInputRef.current.value !== '');

      if (!hasValue) {
        cleanupCancel();
      } else {
        dispatch(hideGlobalBlocking());
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [cleanupCancel, dispatch, fileInputRef, translationFileInputRef]);

  // ─── Browser drag prevention & window-level drag tracking ─────────────────

  useEffect(() => {
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleWindowDragEnter = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) {
        logger.log('[window drag] File drag detected, disabling tool selection');
        setIsFileDragInProgress(true);
      }
    };

    const handleWindowDragLeave = (e: DragEvent) => {
      if (!e.relatedTarget || e.relatedTarget === null) {
        logger.log('[window drag] File drag left window, re-enabling tool selection after delay');
        setTimeout(() => {
          setIsFileDragInProgress(false);
        }, 100);
      }
    };

    const handleWindowDragEnd = (_e: DragEvent) => {
      logger.log('[window drag] Drag operation ended/cancelled, re-enabling tool selection');
      setIsFileDragInProgress(false);
      setIsDraggingOver(false);
      setShowImmediateSpinner(false);
    };

    const handleContextMenu = (_e: MouseEvent) => {
      logger.log('[window drag] Context menu opened, cancelling drag');
      setIsFileDragInProgress(false);
      setIsDraggingOver(false);
      setShowImmediateSpinner(false);
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (isFileDragInProgressRef.current || isDraggingOverRef.current)) {
        logger.log('[window drag] Escape pressed during drag, cancelling', {
          isFileDragInProgress: isFileDragInProgressRef.current,
          isDraggingOver: isDraggingOverRef.current
        });
        setIsFileDragInProgress(false);
        setIsDraggingOver(false);
        setShowImmediateSpinner(false);
        isFileDragInProgressRef.current = false;
        isDraggingOverRef.current = false;
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 2) {
        logger.log('[window drag] Right-click detected, cancelling drag');
        setIsFileDragInProgress(false);
        setIsDraggingOver(false);
        setShowImmediateSpinner(false);
      }
    };

    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('dragend', handleWindowDragEnd);
    window.addEventListener('dragover', preventDefaults);
    window.addEventListener('drop', preventDefaults);
    window.addEventListener('keydown', handleEscapeKey);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('dragend', handleWindowDragEnd);
      window.removeEventListener('dragover', preventDefaults);
      window.removeEventListener('drop', preventDefaults);
      window.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // ─── File Category Helpers ─────────────────────────────────────────────────

  const isImageFile = (f: File | { name: string; type: string }) => /\.(png|jpe?g)$/i.test(f.name) || /image\/(png|jpeg)/.test(f.type);
  const isDataFile = (f: File | { name: string; type: string }) => /\.csv$/i.test(f.name) || f.type === 'text/csv' || f.type === 'application/vnd.ms-excel' || f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || /\.(xls|xlsx)$/i.test(f.name);
  const isTextFile = (f: File | { name: string; type: string }) => {
    return /\.pdf$/i.test(f.name) || f.type === 'application/pdf' ||
           /\.docx?$/i.test(f.name) || f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || f.type === 'application/msword' ||
           /\.pptx?$/i.test(f.name) || f.type === 'application/vnd.ms-powerpoint' || f.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
           /\.msg$/i.test(f.name) || f.type === 'application/vnd.ms-outlook';
  };

  const getFileCategory = useCallback((f: File | { name: string; type: string }) => {
    if (isImageFile(f)) return 'image';
    if (isDataFile(f)) return 'data';
    if (isTextFile(f)) return 'text';
    return 'unknown';
  }, []);

  // ─── getAllowedFileTypes ───────────────────────────────────────────────────

  const getAllowedFileTypes = useMemo(() => {
    if (selectedTool === 'data_analysis') {
      return '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }

    if (selectedTool === 'translation') {
      return '.txt,.html,.csv,.docx,.pptx,.pdf,.msg,.xlsx,text/plain,text/html,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-outlook';
    }

    if (uploadedFiles.length === 0) {
      return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
    }

    const existingCategories = new Set(uploadedFiles.map(f => {
      const mockFile = { name: f.name, type: f.type } as File;
      return getFileCategory(mockFile);
    }));

    if (existingCategories.size > 1 || existingCategories.has('unknown')) {
      return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
    }

    const category = Array.from(existingCategories)[0];

    if (category === 'image') {
      return '.png,.jpg,.jpeg,image/png,image/jpeg';
    } else if (category === 'data') {
      return '.csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    } else if (category === 'text') {
      return '.ppt,.pptx,.pdf,.txt,.doc,.docx,.msg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,application/vnd.ms-outlook';
    }

    return '.png,.jpg,.jpeg,.csv,.xls,.xlsx,.ppt,.pptx,.pdf,.txt,.msg,.doc,.docx,image/png,image/jpeg,text/csv,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-outlook';
  }, [selectedTool, uploadedFiles, getFileCategory]);

  // ─── computeFileLimits helper ─────────────────────────────────────────────

  const computeFileLimits = useCallback((
    incomingFiles: File[],
    existingCount: number,
    opts: { isAnalysisMode: boolean; isTranslationMode: boolean; allImagesCombined: boolean }
  ): {
    maxTotal: number;
    remainingSlots: number;
    hardLimitReached: boolean;
    effectiveFiles: File[];
    warning: { title: string; subtitle?: string | null; message: string; mode: 'general' | 'translation' } | null;
  } => {
    const { isAnalysisMode, isTranslationMode, allImagesCombined } = opts;
    const maxTotal = isTranslationMode ? 5 : isAnalysisMode ? 2 : (allImagesCombined ? 5 : 2);
    const remainingSlots = maxTotal - existingCount;

    if (remainingSlots <= 0) {
      const warning = isTranslationMode
        ? { title: 'Maximum Files Reached', subtitle: 'One or more files could not be added.' as string | null, message: 'You have reached the maximum of 5 files for translation. Remove a file to add another.', mode: 'translation' as const }
        : { title: 'Maximum Files Reached', subtitle: null, message: isAnalysisMode ? 'Maximum of 2 analysis files reached. Remove one to add another.' : allImagesCombined ? 'Maximum of 5 image files reached. Remove one to add another.' : 'Maximum of 2 files reached. Remove one to add another.', mode: 'general' as const };
      return { maxTotal, remainingSlots: 0, hardLimitReached: true, effectiveFiles: [], warning };
    }

    if (incomingFiles.length > remainingSlots) {
      const keptList = incomingFiles.slice(0, remainingSlots).map(f => `- ${f.name}`).join('\n');
      const discardedList = incomingFiles.slice(remainingSlots).map(f => `- ${f.name}`).join('\n');
      const warning = isTranslationMode
        ? { title: 'Maximum Files Reached', subtitle: 'One or more files could not be added.' as string | null, message: `Only ${maxTotal} files are allowed for translation.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`, mode: 'translation' as const }
        : { title: 'Maximum Files Reached', subtitle: null, message: isAnalysisMode
            ? `Only ${maxTotal} files are allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`
            : allImagesCombined
              ? `Only ${maxTotal} image files allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`
              : `Only ${maxTotal} files allowed.\n\nAdded:\n${keptList}\n\nDiscarded:\n${discardedList}`,
          mode: 'general' as const };
      return { maxTotal, remainingSlots, hardLimitReached: false, effectiveFiles: incomingFiles.slice(0, remainingSlots), warning };
    }

    return { maxTotal, remainingSlots, hardLimitReached: false, effectiveFiles: incomingFiles, warning: null };
  }, []);


  // ─── handleFileChange ─────────────────────────────────────────────────────

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    logger.log('[handleFileChange] Function entered', { timestamp: performance.now() });

    const filesList = Array.from(e.target.files || []);

    logger.log('[handleFileChange] Files array created', { fileCount: filesList.length, timestamp: performance.now() });

    if (filesList.length === 0) {
      cleanupCancel();
      return;
    }

    filesBeingProcessedRef.current = true;
    logger.log('[handleFileChange] State after setting processed flag:', {
      filesBeingProcessed: filesBeingProcessedRef.current,
      awaitingFileSelection
    });

    const fileType = (f: File) => f.type || 'application/octet-stream';
    const isExcelCsvFile = (f: File) =>
      fileType(f) === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileType(f) === 'application/vnd.ms-excel' ||
      fileType(f) === 'text/csv' ||
      /\.csv$/i.test(f.name);

    const isExcelCsv = filesList.some(isExcelCsvFile);
    const isTranslationMode = selectedTool === 'translation';

    const warnFn = isTranslationMode
      ? (msg: string) => setFileWarning({ title: 'File Not Accepted', message: msg, mode: 'translation' })
      : (msg: string) => setFileWarning({ message: msg, mode: 'general' });

    const dialogOpen = isFileUploadDialogOpenRef.current;
    // Include both staged (dialog-local) and already-committed uploadedFiles so mixing
    // checks work when the user re-opens the dialog to add another file after a prior commit.
    const existingFiles = dialogOpen
      ? [...stagedFilesRef.current, ...uploadedFiles]
      : uploadedFiles;

    const existingCategories = new Set(existingFiles.map(f => {
      const mockFile = { name: f.name, type: f.type } as File;
      return getFileCategory(mockFile);
    }));

    // Prevent adding CSV/Excel (data) on top of existing image/text files — treat as mixing error
    // before we would otherwise auto-switch into data_analysis mode.
    if (
      isExcelCsv &&
      !isTranslationMode &&
      selectedTool !== 'data_analysis' &&
      existingFiles.length > 0 &&
      (existingCategories.has('image') || existingCategories.has('text'))
    ) {
      const existingCategory = existingCategories.has('image') ? 'image' : 'text';
      const discardedNames = filesList.filter(isExcelCsvFile).map(f => f.name).join(', ');
      warnFn(`Cannot mix data file(s) with existing ${existingCategory} file(s). Discarded: ${discardedNames}`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      cleanupCancel();
      return;
    }

    if (isExcelCsv && selectedTool !== 'translation') {
      dispatch(setSelectedTool('data_analysis'));
      setIsFileUploadActive(false);
    }
    const isAnalysisMode = (selectedTool === 'data_analysis' || isExcelCsv) && !isTranslationMode;

    const incomingCategories = new Set(filesList.map(f => getFileCategory(f as File)));

    // Pre-filter for analysis mode
    if (isAnalysisMode) {
      const csvExcelFiles = filesList.filter(isExcelCsvFile);
      const nonCsvExcel = filesList.filter(f => !isExcelCsvFile(f));
      if (nonCsvExcel.length > 0) {
        const names = nonCsvExcel.map(f => f.name).join(', ');
        // If we're in data_analysis mode because of existing data files (or the tool is
        // already data_analysis) and someone tries to add other file types, this is a
        // mixing error — not an "unsupported type" error.
        const isMixingWithExistingData =
          existingCategories.has('data') || selectedTool === 'data_analysis';
        const message = isMixingWithExistingData
          ? `Cannot mix ${Array.from(new Set(nonCsvExcel.map(f => getFileCategory(f as File)))).filter(c => c !== 'unknown').join(' and ') || 'other'} file(s) with existing data file(s). Discarded: ${names}`
          : `${names}: Unsupported type (CSV/Excel only)`;
        setFileWarning(prev => ({
          title: 'File Not Accepted',
          message: (prev?.message ? prev.message + '\n\n' : '') + message,
          mode: 'general'
        }));
      }
      filesList.splice(0, filesList.length, ...csvExcelFiles);
    }

    // Check for mixing across categories
    if (!isAnalysisMode && !isTranslationMode && incomingCategories.size > 1) {
      const categoryPriority: ('image' | 'data' | 'text')[] = ['image', 'data', 'text'];
      const chosenCategory = categoryPriority.find(cat => incomingCategories.has(cat)) || 'image';

      const kept = filesList.filter(f => getFileCategory(f as File) === chosenCategory);
      const discarded = filesList.filter(f => getFileCategory(f as File) !== chosenCategory);

      const discardedCategoriesSet = new Set(discarded.map(f => getFileCategory(f as File)));
      const discardedCategoryNames = Array.from(discardedCategoriesSet).filter(c => c !== 'unknown').join(' and ');

      warnFn(`Cannot mix file types. Keeping ${chosenCategory} file(s): ${kept.map(f => f.name).join(', ')}. Discarded ${discardedCategoryNames} file(s): ${discarded.map(f => f.name).join(', ')}`);
      filesList.splice(0, filesList.length, ...kept);
    } else if (!isAnalysisMode && !isTranslationMode && existingCategories.size > 0 && incomingCategories.size > 0) {
      const existingCategory = Array.from(existingCategories)[0];
      const incomingCategory = Array.from(incomingCategories)[0];

      if (existingCategory !== incomingCategory && existingCategory !== 'unknown' && incomingCategory !== 'unknown') {
        const discardedNames = filesList.map(f => f.name).join(', ');
        warnFn(`Cannot mix ${incomingCategory} file(s) with existing ${existingCategory} file(s). Discarded: ${discardedNames}`);
        if (fileInputRef.current) fileInputRef.current.value = '';
        cleanupCancel();
        return;
      }
    }

    const allImagesCombined = (!isAnalysisMode && !isTranslationMode) && ((existingCategories.has('image') || existingFiles.length === 0) && filesList.length > 0 && filesList.every(f => isImageFile(f)));
    const { maxTotal: MAX_TOTAL, hardLimitReached, effectiveFiles: limitedFiles, warning: limitWarning } = computeFileLimits(
      filesList,
      existingFiles.length,
      { isAnalysisMode, isTranslationMode, allImagesCombined }
    );
    const effectiveFiles: File[] = limitedFiles;
    const discardedByLimitNames: string[] = limitWarning && !hardLimitReached
      ? filesList.slice(effectiveFiles.length).map(f => f.name)
      : [];

    if (hardLimitReached && limitWarning) setFileWarning(limitWarning);
    if (hardLimitReached) {
      if (isTranslationMode) {
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
      } else {
        cleanupCancel();
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Allowed types differ by mode
    const analysisMime = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    const translationMime = [
      'text/plain',
      'text/html',
      'text/csv',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-outlook'
    ];
    const generalMime = [
      'image/png', 'image/jpeg',
      'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    const MAX_IMAGE_BYTES = MAX_MB * 1024 * 1024;
    const MAX_OTHER_BYTES = 16 * 1024 * 1024;

    const translationFileErrors: Array<{ fileName: string; hasTypeError: boolean; hasSizeError: boolean }> = [];

    const readPromises: Promise<{ name: string; type: string; data: string } | null>[] = effectiveFiles.map(file => {
      return new Promise(resolve => {
        const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel';
        const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
        const isImage = /image\/(png|jpeg)/.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
        const isPpt = /\.pptx?$/i.test(file.name) || file.type === 'application/vnd.ms-powerpoint' || file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        const isPptx = /\.pptx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        const isDoc = /\.docx?$/i.test(file.name) || file.type === 'application/msword' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const isDocx = /\.docx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        const isXlsx = /\.xlsx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const isTxt = /\.txt$/i.test(file.name) || file.type === 'text/plain';
        const isHtml = /\.html?$/i.test(file.name) || file.type === 'text/html';
        const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
        const isMsg = /\.msg$/i.test(file.name) || file.type === 'application/vnd.ms-outlook';

        const allowedSet = isAnalysisMode ? analysisMime : isTranslationMode ? translationMime : generalMime;
        const mimeAllowed = allowedSet.includes(file.type);
        const extAllowedGeneral = isImage || isCsv || isXlsx || isPptx || isDocx || isTxt || isPdf || isMsg;
        const extAllowedAnalysis = isCsv || isExcel;
        const extAllowedTranslation = isTxt || isHtml || isCsv || isDocx || isPptx || isXlsx || isPdf || isMsg;
        const extAllowed = isAnalysisMode ? extAllowedAnalysis : isTranslationMode ? extAllowedTranslation : extAllowedGeneral;
        const isTypeValid = mimeAllowed || extAllowed;

        const isImageStrict = /image\/(png|jpeg)/.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
        const TRANSLATION_MAX_BYTES = 10 * 1024 * 1024;
        const sizeLimit = isImageStrict ? MAX_IMAGE_BYTES : isTranslationMode ? TRANSLATION_MAX_BYTES : MAX_OTHER_BYTES;
        const isSizeValid = file.size <= sizeLimit;

        const errors: string[] = [];

        if (!isTypeValid) {
          errors.push(isAnalysisMode ? 'Unsupported type (CSV/Excel only)' : 'Unsupported file type. Legacy formats (.doc, .ppt, .xls) are not supported — please use .docx, .pptx, or .xlsx');
        }

        if (!isSizeValid) {
          const humanLimit = isImageStrict ? MAX_MB + 'MB image limit' : isTranslationMode ? '10MB limit' : '16MB limit';
          errors.push(isTranslationMode ? `exceeds the 10MB file size limit` : `Exceeds ${humanLimit}`);
        }

        if (errors.length > 0) {
          console.warn('[upload] rejected_file', { name: file.name, size: file.size, type: file.type, errors });
          if (isTranslationMode) {
            translationFileErrors.push({ fileName: file.name, hasTypeError: !isTypeValid, hasSizeError: !isSizeValid });
            return resolve(null);
          }
          const hasTypeError = !isTypeValid;
          const hasSizeError = !isSizeValid;
          const errorMessage = `${file.name}: ${errors.join(' and ')}`;
          setFileWarning(prev => {
            const msg = prev ? prev.message + `\n\n${errorMessage}` : errorMessage;
            const title = prev?.title ?? (hasSizeError && !hasTypeError ? (isImageStrict ? 'Image Too Large' : 'File Too Large') : undefined);
            return { title, message: msg, mode: 'general' };
          });
          return resolve(null);
        }

        const isDuplicate = existingFiles.some(f => f.name === file.name && f.lastModified === file.lastModified);
        if (isDuplicate) {
          console.warn('Duplicate file skipped.', file.name);
          if (isTranslationMode) {
            setFileWarning({ title: 'File Not Accepted', message: `${file.name} has already been added.`, mode: 'translation' });
            setAwaitingFileSelection(false);
            filesBeingProcessedRef.current = false;
          } else {
            const dupMessage = `Duplicate file skipped: ${file.name}`;
            setFileWarning(prev => prev ? { ...prev, message: prev.message + ` | ${dupMessage}` } : { message: dupMessage, mode: 'general' });
          }
          return resolve(null);
        }

        const uniqueKey = `${file.name}_${file.lastModified}_${Math.random().toString(36).substr(2, 9)}`;
        if (dialogOpen) {
          setStagedFiles(prev => [...prev, {
            name: file.name,
            type: file.type,
            data: '',
            size: file.size,
            lastModified: file.lastModified,
            status: 'uploading',
            key: uniqueKey,
          }]);
        }

        const reader = new FileReader();
        if (isTranslationMode) {
          translationActiveReadersRef.current.push(reader);
        } else if (dialogOpen) {
          dialogActiveReadersRef.current.push(reader);
        } else if (!isAnalysisMode) {
          generalActiveReadersRef.current.push(reader);
        }

        logger.log('[FileReader] About to start reading file', { fileName: file.name, fileType: file.type, timestamp: performance.now() });

        reader.onload = () => {
          logger.log('[FileReader] File read complete', { fileName: file.name, timestamp: performance.now() });

          const rawResult = reader.result;
          const prepareMeta = async () => {
            await new Promise<void>(r => setTimeout(r, 0));

            if (!filesBeingProcessedRef.current) {
              resolve(null);
              return;
            }

            let dataString = '';
            if (typeof rawResult === 'string') {
              if (rawResult.startsWith('data:')) {
                const commaIdx = rawResult.indexOf(',');
                dataString = commaIdx >= 0 ? rawResult.substring(commaIdx + 1) : rawResult;
              } else {
                dataString = rawResult;
              }
            } else if (rawResult instanceof ArrayBuffer) {
              dataString = arrayBufferToBase64(rawResult);
            }

            await new Promise<void>(r => setTimeout(r, 0));

            if (!filesBeingProcessedRef.current) {
              resolve(null);
              return;
            }

            let metaFile = { name: file.name, type: file.type, data: dataString };

            if (!isAnalysisMode && !isTranslationMode && (isPpt || isDoc || isTxt) && !isMsg) {
              try {
                const apimBaseUrl = import.meta.env.VITE_APIM_BASE_URL || 'https://navigator.ai.3m.com';
                const conversionEndpoint = import.meta.env.VITE_FILE_CONVERSION_URL
                  || `${apimBaseUrl}/nav-api/api/ai/convert-to-pdf`;

                metaFile = await convertFileToPdf(file, conversionEndpoint, msalInstance);
                if (metaFile.type !== 'application/pdf') {
                  console.warn('[upload] Conversion returned non-PDF result (server conversion likely failed), using original file:', metaFile.type);
                }
              } catch (error) {
                console.error('[upload] Conversion failed, using original file:', error);
              }
            }

            if (!filesBeingProcessedRef.current) {
              resolve(null);
              return;
            }

            const metaRedux = {
              name: metaFile.name,
              size: file.size,
              type: metaFile.type,
              lastModified: file.lastModified,
              data: metaFile.data,
              key: uniqueKey,
              status: 'pending' as const
            };

            if (dialogOpen) {
              setStagedFiles(prev => prev.map(stagedFile => (
                stagedFile.key === uniqueKey
                  ? { ...metaRedux, key: uniqueKey }
                  : stagedFile
              )));
            } else {
              if (isDirectDropBatchActiveRef.current) {
                directDropBatchFileNamesRef.current.push(metaRedux.name);
              }
              dispatch(addUploadedFile(metaRedux));
            }

            resolve(metaFile);
          };
          prepareMeta();
        };

        if (isExcel || isImage || isPpt || isDoc || isPdf || isMsg) {
          logger.log('[FileReader] Starting readAsArrayBuffer (BLOCKING)', { fileName: file.name, timestamp: performance.now() });
          reader.readAsArrayBuffer(file);
        } else {
          logger.log('[FileReader] Starting readAsText', { fileName: file.name, timestamp: performance.now() });
          reader.readAsText(file);
        }
      });
    });

    Promise.all(readPromises).then(results => {
      if (isTranslationMode) {
        translationActiveReadersRef.current = [];
      } else if (dialogOpen) {
        dialogActiveReadersRef.current = [];
      } else if (!isAnalysisMode) {
        generalActiveReadersRef.current = [];
      }

      if (isTranslationMode && translationFileErrors.length > 0) {
        let errorMessage: string;
        if (translationFileErrors.length === 1) {
          const err = translationFileErrors[0];
          if (err.hasTypeError && err.hasSizeError) {
            errorMessage = `${err.fileName} has an unsupported format and exceeds the 10MB size limit.\n\nAccepted formats: .txt, .pdf, .csv, .docx, .pptx, .xlsx, .msg \nNote: Legacy formats (.doc, .ppt, .xls) are not supported.`;
          } else if (err.hasTypeError) {
            errorMessage = `${err.fileName} has an unsupported file format.\n\nAccepted formats: .txt, .pdf, .csv, .docx, .pptx, .xlsx, .msg \nNote: Legacy formats (.doc, .ppt, .xls) are not supported.`;
          } else {
            errorMessage = `${err.fileName} exceeds the 10MB file size limit. Please upload a smaller file.`;
          }
        } else {
          const errorLines = translationFileErrors.map(err => {
            if (err.hasTypeError && err.hasSizeError) return `• ${err.fileName} — unsupported format and exceeds 10MB`;
            if (err.hasTypeError) return `• ${err.fileName} — unsupported file format`;
            return `• ${err.fileName} — exceeds the 10MB file size limit`;
          }).join('\n');
          errorMessage = `The following files could not be added:\n\n${errorLines}\n\nAccepted formats: .txt, .pdf, .csv, .docx, .pptx, .xlsx, .msg\nNote: Legacy formats (.doc, .ppt, .xls) are not supported.`;
        }
        setFileWarning({ title: 'File Not Accepted', subtitle: translationFileErrors.length > 1 ? null : undefined, message: errorMessage, mode: 'translation' });
        const validResults = results.filter(Boolean);
        if (validResults.length === 0) {
          setAwaitingFileSelection(false);
          filesBeingProcessedRef.current = false;
          dispatch(hideGlobalBlocking());
          return;
        }
      }

      const metas = results.filter(Boolean) as { name: string; type: string; data: string }[];

      if (discardedByLimitNames.length > 0 && metas.length > 0) {
        const discardedList = discardedByLimitNames.map(n => `- ${n}`).join('\n');
        const addedList = metas.map(m => `- ${m.name}`).join('\n');
        setFileWarning(prev => {
          const base = prev
            ? prev
            : { title: 'Maximum Files Reached', message: '', mode: 'general' as const };
          const limitNote = `Only ${MAX_TOTAL} files are allowed.\n\nAdded:\n${addedList}\n\nDiscarded:\n${discardedList}`;
          return { ...base, title: 'Maximum Files Reached', message: base.message ? `${base.message}\n\n${limitNote}` : limitNote };
        });
      }

      if (metas.length > 0) {
        if (isAnalysisMode) {
          if (dialogOpen) {
            logger.log('[handleFileChange] Data analysis mode - files staged for dialog confirmation');
            requestAnimationFrame(() => {
              setAwaitingFileSelection(false);
              filesBeingProcessedRef.current = false;
              dispatch(hideGlobalBlocking());
            });
          } else {
            logger.log('[handleFileChange] Data analysis mode - uploading files immediately (drag-and-drop or direct picker)');
            uploadFilesBatch(metas);
          }
        } else {
          logger.log('[handleFileChange] General chat mode - staging locally:', metas.map(m => m.name));
          logger.log('[handleFileChange] Clearing awaiting states - files now in Redux');
          setTimeout(() => {
            setAwaitingFileSelection(false);
            filesBeingProcessedRef.current = false;
            dispatch(hideGlobalBlocking());
          }, 1000);
        }
      } else {
        logger.log('[handleFileChange] No valid files - clearing all states');
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedTool, uploadedFiles]);

  // ─── processPastedImagesFromFiles ─────────────────────────────────────────

  const processPastedImagesFromFiles = useCallback((imageFiles: File[]) => {
    try {
      if (imageFiles.length === 0) return;

      const validFiles: File[] = [];

      for (const file of imageFiles) {
        const existingNonImages = uploadedFiles.some(f => !(/image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name)));
        if (existingNonImages) {
          setFileWarning(prev => prev ? { ...prev, message: prev.message + ' | Cannot paste image while non-image files attached.' } : { message: 'Cannot paste image while non-image files are already attached.', mode: 'general' });
          return;
        }

        const existingImagesCount = uploadedFiles.filter(f => (/image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name))).length;
        const remainingSlots = 5 - existingImagesCount;

        if (validFiles.length >= remainingSlots) {
          setFileWarning(prev => prev ? { ...prev, message: prev.message + ' | Image limit reached (5).' } : { message: 'Image limit reached (5). Remove one to paste another.', mode: 'general' });
          break;
        }

        const MAX_IMAGE_BYTES = MAX_MB * 1024 * 1024;
        if (file.size > MAX_IMAGE_BYTES) {
          setFileWarning(prev => prev ? { ...prev, message: prev.message + ` | Pasted image too large (>${MAX_MB}MB): ${file.name}` } : { message: `Pasted image exceeds ${MAX_MB}MB and was skipped: ${file.name}`, mode: 'general' });
          continue;
        }

        const isDuplicate = uploadedFiles.some(f => f.name === file.name);
        if (isDuplicate) {
          setFileWarning(prev => prev ? { ...prev, message: prev.message + ` | Duplicate pasted image skipped: ${file.name}` } : { message: `Duplicate pasted image skipped: ${file.name}`, mode: 'general' });
          continue;
        }

        validFiles.push(file);
      }

      if (validFiles.length === 0) return;

      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result;
          let dataString = '';
          if (typeof result === 'string') {
            if (result.startsWith('data:')) {
              const commaIdx = result.indexOf(',');
              dataString = commaIdx >= 0 ? result.substring(commaIdx + 1) : result;
            } else {
              dataString = result;
            }
          } else if (result instanceof ArrayBuffer) {
            dataString = arrayBufferToBase64(result);
          }
          const uniqueKey = `${file.name}_${file.lastModified}_${Math.random().toString(36).substr(2, 9)}`;
          dispatch(addUploadedFile({
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
            data: dataString,
            key: uniqueKey,
            status: 'pending'
          }));
        };
        reader.readAsArrayBuffer(file);
      });
    } catch (err) {
      console.warn('[paste-images-processing] failed', err);
    }
  }, [dispatch, uploadedFiles]);

  // ─── handlePaste ──────────────────────────────────────────────────────────

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    try {
      if (isStreaming) return;

      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;

      const itemsArray = Array.from(items);
      const imageItems = itemsArray.filter(item => item.type?.startsWith("image/"));
      const textItems = itemsArray.filter(
        item => item.type === "text/plain" || item.type === "text/rtf"
      );

      if (imageItems.length === 0 || textItems.length > 0) return;

      if (shouldHideTools) {
        e.preventDefault();
        return;
      }

      if (isRagEnabled) {
        setFileWarning(prev =>
          prev
            ? { ...prev, message: prev.message + ' | File uploads are not allowed while a RAG chat is active.' }
            : { message: 'File uploads are not allowed while a RAG chat is active.', mode: 'general' }
        );
        e.preventDefault();
        return;
      }

      if (effectiveToolForSend === 'image_generation') {
        setFileWarning(prev =>
          prev
            ? { ...prev, message: prev.message + ' | Image Generation does not support file uploads, including images.' }
            : { message: 'Image Generation tool does not support pasted images.', mode: 'general' }
        );
        e.preventDefault();
        return;
      }

      if (selectedTool === 'data_analysis') {
        setFileWarning(prev =>
          prev
            ? { ...prev, message: prev.message + ' | Data Analysis only supports CSV or Excel files.' }
            : { message: 'Data Analysis tool only supports CSV or Excel files. Pasted images are not allowed.', mode: 'general' }
        );
        e.preventDefault();
        return;
      }

      e.preventDefault();

      const imageFiles: File[] = imageItems.map(item => {
        const blob = item.getAsFile();
        if (!blob) return null;

        const ext =
          item.type === 'image/png'
            ? 'png'
            : item.type === 'image/jpeg'
            ? 'jpg'
            : 'img';

        return new File(
          [blob],
          `pasted_image_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`,
          { type: item.type }
        );
      }).filter(Boolean) as File[];

      processPastedImagesFromFiles(imageFiles);
    } catch (err) {
      console.warn('[paste-images] failed', err);
    }
  }, [isStreaming, shouldHideTools, isRagEnabled, effectiveToolForSend, selectedTool, processPastedImagesFromFiles]);

  // ─── handleRemoveFile ─────────────────────────────────────────────────────

  const handleRemoveFile = useCallback((name: string) => {
    const doRemove = () => {
      const willBeEmpty = uploadedFiles.length === 1;
      dispatch(removeUploadedFile(name));
      if (fileInputRef.current) fileInputRef.current.value = '';

      if (selectedTool === 'data_analysis') {
        dispatch(setSelectedTool(null));
        manuallyClearedToolRef.current = true;
        if (onClearChat) {
          onClearChat();
        }
      }

      setIsCompareActive(false);

      if (willBeEmpty) {
        setIsFileUploadActive(false);
      }
    };

    doRemove();
  }, [dispatch, selectedTool, onClearChat, fileInputRef, uploadedFiles.length, manuallyClearedToolRef, setIsCompareActive, setIsFileUploadActive]);

  // ─── handleClearAllFiles ──────────────────────────────────────────────────

  const handleClearAllFiles = useCallback(() => {
    if (hasMessages && selectedTool === 'data_analysis' && onClearChat) {
      onClearChat();
    }

    dispatch(clearUploadedFiles());
    dispatch(clearTargetLanguages());
    if (fileInputRef.current) fileInputRef.current.value = '';

    setIsFileUploadActive(false);
    setAwaitingFileSelection(false);
    filesBeingProcessedRef.current = false;
    dispatch(hideGlobalBlocking());
    setIsCompareActive(false);

    if (selectedTool === 'data_analysis') {
      dispatch(setSelectedTool(null));
      manuallyClearedToolRef.current = true;
      dispatch(clearTranslationData());
      setIsTranslationCollapsed(false);
    }

    if (selectedTool === 'translation') {
      dispatch(clearTargetLanguages());
    }
  }, [dispatch, selectedTool, hasMessages, onClearChat, fileInputRef, manuallyClearedToolRef, setIsFileUploadActive, setIsCompareActive, setIsTranslationCollapsed]);

  // ─── Dialog handlers ──────────────────────────────────────────────────────

  const handleFileUploadDialogDropzoneClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      setHideFileUploadDialogDropzone(false);

      logger.log('[handleFileUploadDialogDropzoneClick] Setting awaiting states and showing spinner');
      setAwaitingFileSelection(true);
      filesBeingProcessedRef.current = false;

      fileInputRef.current.click();
    }
  }, [fileInputRef]);

  const handleFileUploadDialogUpload = useCallback(() => {
    logger.log('[handleFileUploadDialogUpload] Confirming upload of staged files:', stagedFiles.map(f => f.name));
    const readyFiles = stagedFiles.filter(file => file.status !== 'uploading' && !!file.data);
    if (readyFiles.length === 0) {
      return;
    }

    readyFiles.forEach(file => dispatch(addUploadedFile(file)));

    const isAnalysisMode = selectedTool === 'data_analysis';
    if (isAnalysisMode && readyFiles.length > 0) {
      setAwaitingFileSelection(true);
      filesBeingProcessedRef.current = true;
      const metas = readyFiles.map(f => ({ name: f.name, type: f.type, data: f.data }));
      uploadFilesBatch(metas);
    }

    setStagedFiles([]);
    setHideFileUploadDialogDropzone(false);
    setIsFileUploadDialogOpen(false);
  }, [stagedFiles, dispatch, selectedTool, uploadFilesBatch, setIsFileUploadDialogOpen]);

  const handleFileUploadDialogClose = useCallback(() => {
    logger.log('[handleFileUploadDialogClose] Canceling and discarding staged files:', stagedFiles.map(f => f.name));

    setStagedFiles([]);
    setAwaitingFileSelection(false);
    filesBeingProcessedRef.current = false;
    dispatch(hideGlobalBlocking());

    if (selectedTool === 'data_analysis' || selectedTool === 'translation') {
      manuallyClearedToolRef.current = true;
      dispatch(setSelectedTool(null));
      dispatch(clearTranslationData());
      setIsTranslationCollapsed(false);
    }

    if (uploadedFiles.length === 0) {
      setIsFileUploadActive(false);
    }

    setHideFileUploadDialogDropzone(false);
    setIsFileUploadDialogOpen(false);
  }, [stagedFiles, dispatch, selectedTool, uploadedFiles.length, manuallyClearedToolRef, setIsFileUploadActive, setIsTranslationCollapsed, setIsFileUploadDialogOpen]);

  const handleFileUploadDialogCancelFileRead = useCallback(() => {
    abortAndClearReaders(dialogActiveReadersRef);
    filesBeingProcessedRef.current = false;
    setAwaitingFileSelection(false);
    setStagedFiles([]);
    setHideFileUploadDialogDropzone(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    dispatch(hideGlobalBlocking());
  }, [dispatch, fileInputRef]);

  const handleFilesDroppedOnDialog = useCallback((files: File[]) => {
    if (!files.length) return;
    const dt = new DataTransfer();
    files.forEach(f => dt.items.add(f));
    if (fileInputRef.current) {
      fileInputRef.current.files = dt.files;
    }
    const syntheticEvent = {
      target: { files: dt.files },
      nativeEvent: new Event('change'),
      currentTarget: fileInputRef.current,
      bubbles: true,
      cancelable: false,
      defaultPrevented: false,
      eventPhase: 0,
      isTrusted: false,
      preventDefault: () => {},
      isDefaultPrevented: () => false,
      stopPropagation: () => {},
      isPropagationStopped: () => false,
      persist: () => {},
      timeStamp: Date.now(),
      type: 'change',
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleFileChange(syntheticEvent);
  }, [handleFileChange, fileInputRef]);

  const handleCancelDirectDropUpload = useCallback(() => {
    abortAndClearReaders(generalActiveReadersRef);
    filesBeingProcessedRef.current = false;
    setAwaitingFileSelection(false);
    setShowImmediateSpinner(false);
    setIsDraggingOver(false);
    dispatch(hideGlobalBlocking());

    Array.from(new Set(directDropBatchFileNamesRef.current)).forEach(name => {
      dispatch(removeUploadedFile(name));
    });

    directDropBatchFileNamesRef.current = [];
    isDirectDropBatchActiveRef.current = false;

    manuallyClearedToolRef.current = true;
    dispatch(setSelectedTool(null));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [dispatch, fileInputRef, manuallyClearedToolRef]);

  const handleRemoveStagedFile = useCallback((fileName: string) => {
    logger.log('[handleRemoveStagedFile] Removing staged file:', fileName);
    setStagedFiles(prev => prev.filter(f => f.name !== fileName));
  }, []);

  // ─── Drag and drop handlers for ChatInput ─────────────────────────────────

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isStreaming || globalBlocking || isRagEnabled || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    if (isFileUploadDialogOpen) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    if (awaitingFileSelection || filesBeingProcessedRef.current) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    e.dataTransfer.dropEffect = 'copy';

    if (selectedTool === 'translation') return;

    setIsDraggingOver(true);
  }, [isStreaming, globalBlocking, selectedTool, isFileUploadDialogOpen, awaitingFileSelection, isRagEnabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const relatedTarget = e.relatedTarget as Node | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDraggingOver(false);
      setShowImmediateSpinner(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isStreaming || globalBlocking || isRagEnabled || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    if (isFileUploadDialogOpen) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    if (awaitingFileSelection || filesBeingProcessedRef.current) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    e.dataTransfer.dropEffect = 'copy';

    if (selectedTool === 'translation') return;

    if (!isDraggingOver) setIsDraggingOver(true);
  }, [isStreaming, globalBlocking, selectedTool, isFileUploadDialogOpen, awaitingFileSelection, isDraggingOver, isRagEnabled]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsFileDragInProgress(false);

    if (isStreaming || globalBlocking || isRagEnabled || ['image_generation', 'web_search', 'reasoning'].includes(selectedTool || '')) {
      setIsDraggingOver(false);
      e.dataTransfer.dropEffect = 'none';
      setShowBlockingMessage(true);
      setTimeout(() => setShowBlockingMessage(false), 3000);
      return;
    }

    if (isFileUploadDialogOpen) {
      setIsDraggingOver(false);
      e.dataTransfer.dropEffect = 'none';
      return;
    }

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      logger.log('[handleDrop] Drop event triggered', { fileCount: files.length, timestamp: performance.now() });

      if (selectedTool === 'translation') {
        setIsDraggingOver(false);
        if (translationFileInputRef.current) {
          setAwaitingFileSelection(true);
          filesBeingProcessedRef.current = true;
          const dataTransfer = new DataTransfer();
          Array.from(files).forEach(file => dataTransfer.items.add(file));
          translationFileInputRef.current.files = dataTransfer.files;
          const event = new Event('change', { bubbles: true });
          translationFileInputRef.current.dispatchEvent(event);
        }
        return;
      }

      const currentFileCount = uploadedFiles.length;
      const isAnalysisMode = selectedTool === 'data_analysis';
      const isDirectGeneralChatDrop = !selectedTool;

      const droppedFilesArray = Array.from(files);
      const existingAreAllImages = uploadedFiles.length === 0 || uploadedFiles.every(f => /image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name));
      const droppedAreAllImages = droppedFilesArray.every(f => /image\/(png|jpeg)/.test(f.type) || /\.(png|jpe?g)$/i.test(f.name));
      const allImagesCombined = !isAnalysisMode && existingAreAllImages && droppedAreAllImages && droppedFilesArray.length > 0;
      const { hardLimitReached: dropHardLimit, warning: dropLimitWarning } = computeFileLimits(
        droppedFilesArray,
        currentFileCount,
        { isAnalysisMode, isTranslationMode: false, allImagesCombined }
      );

      if (dropHardLimit) {
        if (dropLimitWarning) setFileWarning(dropLimitWarning);
        setIsDraggingOver(false);
        return;
      }

      logger.log('[handleDrop] Converting drag overlay to spinner immediately', { timestamp: performance.now() });

      if (isDirectGeneralChatDrop) {
        directDropBatchFileNamesRef.current = [];
        isDirectDropBatchActiveRef.current = true;
        setStagedFiles([]);
        setHideFileUploadDialogDropzone(true);
        isFileUploadDialogOpenRef.current = true;
        setIsFileUploadDialogOpen(true);
        setShowImmediateSpinner(false);
        setIsDraggingOver(false);
      } else {
        setIsDraggingOver(false);
      }
      setAwaitingFileSelection(true);
      filesBeingProcessedRef.current = true;

      logger.log('[handleDrop] Starting file processing immediately', { timestamp: performance.now() });

      const dataTransfer = new DataTransfer();
      droppedFilesArray.forEach(file => dataTransfer.items.add(file));

      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        const event = new Event('change', { bubbles: true });
        fileInputRef.current.dispatchEvent(event);
      }

      if (!isDirectGeneralChatDrop) {
        dispatch(showGlobalBlocking('Processing files...'));
      }

      logger.log('[handleDrop] File input event dispatched', { timestamp: performance.now() });

      if (!isDirectGeneralChatDrop) {
        setTimeout(() => {
          setShowImmediateSpinner(false);
          setIsDraggingOver(false);
        }, 150);
      }
    } else {
      setIsDraggingOver(false);
    }
  }, [isStreaming, globalBlocking, selectedTool, isFileUploadDialogOpen, uploadedFiles, computeFileLimits, dispatch, fileInputRef, translationFileInputRef, setIsFileUploadDialogOpen, isRagEnabled]);

  // ─── Unified overlay logic effect ─────────────────────────────────────────

  useEffect(() => {
    const hasFiles = uploadedFiles.length > 0;
    const isAnalysis = selectedTool === 'data_analysis';
    const filesIncoming = filesBeingProcessedRef.current;

    if (!hasFiles && !awaitingFileSelection && !filesIncoming) {
      dispatch(hideGlobalBlocking());
      return;
    }
    if (uploadError) return;

    const anyError = uploadedFiles.some(f => f.status === 'error');
    const allUploaded = uploadedFiles.length > 0 && uploadedFiles.every(f => f.status === 'uploaded');

    if (isAnalysis) {
      if (allUploaded || anyError) {
        setAwaitingFileSelection(false);
        filesBeingProcessedRef.current = false;
        dispatch(hideGlobalBlocking());
      }
    } else {
      if (awaitingFileSelection && (hasFiles || filesIncoming)) {
        if (hasFiles && !isDirectDropBatchActiveRef.current) {
          setAwaitingFileSelection(false);
        }
      }
    }
  }, [selectedTool, uploadedFiles, dispatch, uploadError, awaitingFileSelection]);

  // ─── Return ────────────────────────────────────────────────────────────────

  return {
    // State
    stagedFiles,
    setStagedFiles,
    uploadError,
    setUploadError,
    fileWarning,
    setFileWarning,
    isDataAnalysisUploading,
    showImmediateSpinner,
    setShowImmediateSpinner,
    awaitingFileSelection,
    setAwaitingFileSelection,
    filesBeingProcessedRef,
    isDraggingOver,
    setIsDraggingOver,
    isFileDragInProgress,
    setIsFileDragInProgress,
    showBlockingMessage,
    setShowBlockingMessage,
    hideFileUploadDialogDropzone,
    setHideFileUploadDialogDropzone,

    // Callbacks
    handleFileChange,
    handlePaste,
    handleRemoveFile,
    handleClearAllFiles,
    uploadFilesBatch,
    cleanupCancel,
    getAllowedFileTypes,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleCancelDirectDropUpload,
    handleRemoveStagedFile,
    handleFilesDroppedOnDialog,
    handleFileUploadDialogDropzoneClick,
    handleFileUploadDialogUpload,
    handleFileUploadDialogClose,
    handleFileUploadDialogCancelFileRead,
    processPastedImagesFromFiles,
    computeFileLimits,
    // Refs
    uploadedFilesRef,
    isFileUploadDialogOpenRef,
    stagedFilesRef,
    translationActiveReadersRef,
    dialogActiveReadersRef,
    generalActiveReadersRef,
    directDropBatchFileNamesRef,
    isDirectDropBatchActiveRef,
    isFileDragInProgressRef,
    isDraggingOverRef,
  };
}
